import type { DtcEntry } from "@strtmn/j1939"
import type { MachineFamily } from "@strtmn/sds/types"
import { toHex, toLocalStringDe } from "@strtmn/strtmn"
import { get } from "aws-amplify/api"
import { I18n } from "aws-amplify/utils"
import {
  Chart as ChartJS,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js"
import "chartjs-adapter-luxon"
import { useEffect, useState } from "react"
import { Line } from "react-chartjs-2"
import { useParams } from "react-router-dom"
import DateTimePicker from "react-tailwindcss-datetimepicker"
import { ErrorBanner, LoadingErrorState } from "../components"
import VehiclePageLayout from "../components/VehiclePageLayout"
import { DtcTable } from "../components/vehicle-debug/Fehlerliste"
import { isDev, isProd } from "../config"
import { useMachineFromUserCtx } from "../hooks"
import { currentSession } from "../Utils.AmplifyAuth"

import "./TimeseriesPage.css"

ChartJS.register(
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip
)

type TimeseriesPageMode = "pgn" | "dtcs" | "presence" | "location"
type TimeseriesId = {
  vin: string
  pgn: number
  sourceAddress: number
  spn: string
}
/** @note Type also exists in backend (fe-api / telemetry) */
type NumericTimeseriesEntry = {
  Timestamp: number
  Value: string | number | any
}
/** @note Type also exists in backend (fe-api / telemetry) */
type DtcTimeseriesEntry = {
  FailureMode: number
  FailureModeName: string
  IsActive: boolean
  MalfunctionLamp: number
  MalfunctionLampStatusName: string
  Occurence: number
  ProtectLamp: number
  ProtectLampStatusName: string
  SourceAddress: number | string
  SPN: number | string
  StopLamp: number
  StopLampStatusName: string
  Timestamp: number
  WarningLamp: number
  WarningLampStatusName: string
}

type TimeseriesPageHeaderProps = TimeseriesId & {
  mode: TimeseriesPageMode
  begin: Date
  end: Date
}
const TimeseriesPageHeader = ({
  mode,
  vin,
  pgn,
  sourceAddress,
  spn,
  begin,
  end,
}: TimeseriesPageHeaderProps) => {
  return (
    <div className="overflow-x-auto">
      <table>
        <tbody>
          {/* <div>
        VIN: <Link to={getLinkToVehicleDebug({ vin: vin })}>{vin}</Link>
      </div> */}
          {mode === "dtcs" ? (
            <tr>
              <td>PGN:</td>
              <td>DTCs</td>
            </tr>
          ) : (
            <>
              <tr>
                <td>PGN:</td>
                <td>{toHex(pgn, 4)}</td>
              </tr>
              <tr>
                <td>Source Address:</td>
                <td>{toHex(sourceAddress, 4)}</td>
              </tr>
              <tr>
                <td>SPN:</td>
                <td>{spn}</td>
              </tr>
            </>
          )}
          <tr>
            <td>Zeitraum:</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

type TimeseriesPageChartProps = {
  dataWithMeta: TimeseriesId & {
    [meta: string]: any
    data: NumericTimeseriesEntry[]
  }
}
const TimeseriesPageChart = ({
  dataWithMeta,
  ...props
}: TimeseriesPageChartProps) => {
  const barThickness = 6
  // const barPercentage = 8
  const title = `${dataWithMeta.spn}`

  const rows = dataWithMeta.data
  console.log("dataWithMeta", dataWithMeta)

  const xLabels = rows.map((row) => {
    const dtUtc = new Date(row.Timestamp).toISOString()
    return dtUtc
  })
  console.log("xLabels", xLabels)

  const tsValues = rows.map((row) => {
    return Number(row.Value)
  })
  console.log("tsValues", tsValues)

  const cdata = {
    labels: xLabels,
    datasets: [
      {
        label: title,
        data: tsValues,
        borderWidth: 1,
        barThickness: barThickness,
        // barPercentage: barPercentage,
        borderColor: "rgb(54, 162, 235)",
        backgroundColor: "rgba(54, 162, 235, 0.5)",
      },
    ],
  }

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: `${dataWithMeta["begin"]} -- ${dataWithMeta["end"]}`,
      },
    },
    scales: {
      x: {
        bounds: "data" as const,
        // suggestedMin: dataWithMeta.begin,
        min: dataWithMeta["begin"],
        // suggestedMax: dataWithMeta.end,
        max: dataWithMeta["end"],
        type: "time" as const,
        ticks: {
          source: "auto" as const,
          /** @note without a callback, the chart collapses every now and then */
          callback: (value: any) => {
            const dt = new Date(value)
            return (
              ("0" + dt.getDate()).slice(-2) +
              "." +
              ("0" + (dt.getMonth() + 1)).slice(-2) +
              ". " +
              ("0" + dt.getHours()).slice(-2) +
              ":" +
              ("0" + dt.getMinutes()).slice(-2)
            )
          },
        },
      },
    },
  }

  return (
    <div>
      <div>
        {dataWithMeta.data.length} Werte in der Zeitreihe
        {dataWithMeta["maybeMoreItems"] ? (
          <> (vermutlich nicht vollständig)</>
        ) : (
          <></>
        )}
      </div>

      <Line className="timeseries-chart" data={cdata} options={options} />
    </div>
  )
}

type TimeseriesPageListProps = {
  machineFamily?: MachineFamily
  dataWithMeta: TimeseriesId & {
    [meta: string]: any
    data: DtcTimeseriesEntry[]
  }
}
const TimeseriesPageList = ({
  machineFamily,
  dataWithMeta,
  ...props
}: TimeseriesPageListProps) => {
  const rows = dataWithMeta.data as unknown as DtcEntry[]
  console.log("# dtcs:", rows.length)
  console.log("dataWithMeta", dataWithMeta)
  return <DtcTable dtcsSorted={rows} machineFamily={machineFamily} />
}

type TimeseriesPageProps = {
  mode: TimeseriesPageMode
}
export default function TimeseriesPage({ mode }: TimeseriesPageProps) {
  const params = useParams<{
    vin: string
    sourceAddress: string
    pgn: string
    spn: string
  }>()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const [data, setData] = useState<any | undefined>(undefined)

  const [vin, setVin] = useState<string>("")
  const [pgn, setPgn] = useState<number>(-1)
  const [sourceAddress, setSourceAddress] = useState<number>(-1)
  const [spn, setSpn] = useState<string>("")

  const machineFromUserCtx = useMachineFromUserCtx(params.vin!)

  const now = new Date()
  const defaultDaysBack = 1
  const defaultBegin = new Date(
    now.getTime() - defaultDaysBack * 24 * 3600 * 1000
  )
  const defaultEnd = now
  const [begin, setBegin] = useState<Date>(defaultBegin)
  const [end, setEnd] = useState<Date>(defaultEnd)

  const [selectedRange, setSelectedRange] = useState({
    start: defaultBegin,
    end: defaultEnd,
  })

  function handleApply(startDate: Date, endDate: Date) {
    setSelectedRange({ start: startDate, end: endDate })
    setBegin(startDate)
    setEnd(endDate)
  }

  useEffect(() => {
    // This effect parses the params into state
    setVin(params.vin!)
    setPgn(mode === "dtcs" ? -1 : Number(params.pgn!))
    setSourceAddress(mode === "dtcs" ? -1 : Number(params.sourceAddress!))
    setSpn(mode === "dtcs" ? "" : params.spn!)
  }, [params, mode])

  useEffect(() => {
    // This effect uses state to load data
    const getData = async () => {
      const tokens = await currentSession()
      const apiName = "frontendApi"
      const path =
        mode === "dtcs"
          ? `/timeseries/${vin}/dtcs?begin=${begin!.toISOString()}&end=${end!.toISOString()}`
          : `/timeseries/${vin}/${pgn}/${sourceAddress}/${spn}?begin=${begin!.toISOString()}&end=${end!.toISOString()}`
      // console.log('path', path)
      try {
        const options = {
          headers: {
            Authorization: `Bearer ${tokens?.idToken}`,
          },
        }
        console.debug(`Sending API call`)
        const restOperation = get({ apiName, path, options })
        const response = await (await restOperation.response).body.json()
        // console.log(response)

        setData(response)
        setError(undefined)
      } catch (err) {
        console.error("Error fetching data from api")
        if (isDev) {
          console.error(
            `Tried to fetch Timeseries data from ${apiName} :: ${path}`
          )
        }
        let errMsg: string
        /** @ts-ignore */
        errMsg = err!.response!.data!.message!
        if (!errMsg) {
          errMsg = err instanceof Error ? err.message : String(err)
        }
        console.error(errMsg)
        setData(undefined)
        setError(errMsg)
      } finally {
        setLoading(false)
      }
    }

    if (vin && vin !== "never-set") {
      getData()
    }
  }, [mode, vin, pgn, sourceAddress, spn, begin, end])

  if (loading || error) {
    return (
      <VehiclePageLayout vin={params.vin!}>
        <LoadingErrorState
          isLoading={loading}
          loadingMessage={I18n.get("Lade Daten...")}
          isError={!!error}
          errorMessage={
            isProd
              ? I18n.get("Fehler beim Laden der Daten.")
              : `Fehler beim Laden der Daten: ${error}`
          }
        />
      </VehiclePageLayout>
    )
  }
  if (!data || !machineFromUserCtx) {
    return (
      <VehiclePageLayout vin={params.vin!}>
        <ErrorBanner
          error={I18n.get("Fehler beim Laden der Daten: Keine Daten gefunden")}
        />
      </VehiclePageLayout>
    )
  }

  const startOfYesterday = new Date()
  startOfYesterday.setDate(startOfYesterday.getDate() - 1)
  startOfYesterday.setHours(0, 0, 0, 0)
  const endOfYesterday = new Date(startOfYesterday)
  endOfYesterday.setDate(endOfYesterday.getDate() + 1)
  endOfYesterday.setSeconds(endOfYesterday.getSeconds() - 1)

  const vehicleFromDdb = machineFromUserCtx.vehicleFromDdb
  const machineType: string =
    machineFromUserCtx.machine_type || vehicleFromDdb?.["m_machineType"] || ""
  const machineTypeLc = machineType.toLowerCase()

  let machineFamily: MachineFamily | undefined
  if (machineTypeLc.startsWith("p")) {
    machineFamily = "primus"
  } else if (
    machineTypeLc.startsWith("everti") ||
    machineTypeLc.startsWith("e-verti")
  ) {
    machineFamily = "e-vertifeed"
  } else if (
    machineTypeLc.startsWith("eprimus") ||
    machineTypeLc.startsWith("e-primus")
  ) {
    machineFamily = "e-primus"
  }

  return (
    <VehiclePageLayout vin={params.vin!}>
      <h2>Zeitreihe</h2>

      <TimeseriesPageHeader
        mode={mode}
        vin={params["vin"]!}
        pgn={pgn}
        sourceAddress={sourceAddress}
        spn={spn}
        begin={begin!}
        end={end!}
      />

      <DateTimePicker
        // locale={}
        ranges={{
          "Letzte 24 Std.": [defaultBegin, defaultEnd],
          Gestern: [startOfYesterday, endOfYesterday],
          "Letzte 7 Tage": [
            new Date(now.getTime() - 7 * 24 * 3600 * 1000),
            defaultEnd,
          ],
          "Letzte 30 Tage": [
            new Date(now.getTime() - 30 * 24 * 3600 * 1000),
            defaultEnd,
          ],
        }}
        start={selectedRange.start}
        end={selectedRange.end}
        applyCallback={handleApply}
        locale={{
          cancel: "Abbrechen",
          apply: "Übernehmen",
          close: "Schließen",
          fromDate: "Start",
          toDate: "Ende",
          format: "dd.MM.yyyy HH:mm", // See: https://date-fns.org/v2.16.1/docs/format
        }}
        maxDate={now}
        years={[2024, now.getFullYear()]}
        smartMode={true}
        pastSearchFriendly={true}
        standalone={true}
      >
        <button type="button">{`${toLocalStringDe(selectedRange.start.toISOString())} -- ${toLocalStringDe(selectedRange.end.toISOString())}`}</button>
      </DateTimePicker>

      {0 === data.data.length ? (
        <>Keine Datenpunkte im Zeitraum.</>
      ) : (
        <>
          {mode === "dtcs" ? (
            <TimeseriesPageList
              dataWithMeta={data}
              machineFamily={machineFamily}
            />
          ) : (
            <TimeseriesPageChart dataWithMeta={data} />
          )}
        </>
      )}
    </VehiclePageLayout>
  )
}
